import { getAuthConfig } from './amplify-config';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, getCurrentUser, fetchAuthSession, type AuthUser, type AuthTokens, fetchUserAttributes } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import Time from '../utils/time';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { tokenCache } from './tokenCache';
import { authLogger } from './authLogger';

/**
 * Ensures the user is authenticated.
 * If authenticated, return auth object. Otherwise force authentication.
 * Auth object will be used to call API gateway
 */
export const ensureAuthenticated = async (): Promise<AuthUser> => {
    authLogger.debug('Ensuring authentication...');
    const config = getAuthConfig();
    await Amplify.configure({
        Auth: {
            Cognito: {
                ...config.Cognito,
                loginWith: {
                    oauth: {
                        ...config.Cognito.loginWith.oauth,
                        responseType: 'code' as const
                    }
                }
            }
        }
    });

    let userData: AuthUser;

    try {
        userData = await getCurrentUser();
        authLogger.debug('User already authenticated');
    } catch (error) {
        authLogger.info('User not authenticated, redirecting to sign in...');
        await signInWithRedirect({
            provider: { custom: 'AmazonFederate' }
        });
        userData = await getCurrentUser();
        authLogger.info('User successfully authenticated after redirect');
    }

    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload as { email: string; identities: { userId: string }[] } | undefined;

    const userId = payload?.identities?.[0]?.userId;
    if (userId) {
        sessionStorage.setItem('cognitoEmail', `${userId}@amazon.com`);
        sessionStorage.setItem('cognitoUserName', userId);
    }

    return userData;
}

export const getValidToken = async (): Promise<string> => {
    // Check if there's an ongoing refresh
    const existingRefresh = tokenCache.getRefreshPromise();
    if (existingRefresh) {
        authLogger.debug('Using existing refresh promise');
        return existingRefresh;
    }

    // Check cache first
    const cachedToken = tokenCache.getToken('idToken');
    if (cachedToken && !tokenCache.isTokenExpiringSoon('idToken')) {
        authLogger.debug('Using cached token');
        return cachedToken;
    }

    authLogger.debug('No valid cached token, initiating refresh');

    // If no cached token or it's expiring soon, get a new one
    const refreshPromise = (async () => {
        try {
            const session = await fetchAuthSession();
            const tokens = session.tokens as AuthTokens | undefined;
            const idToken = tokens?.idToken;

            if (!idToken) {
                throw new Error('No ID token available');
            }

            const jwtToken = idToken.toString();
            const tokenExpirationMs = Time.seconds(idToken.payload.exp || 0);

            // Cache the token
            tokenCache.setToken('idToken', jwtToken, tokenExpirationMs);
            sessionStorage.setItem('token', jwtToken);
            sessionStorage.setItem('credentials', JSON.stringify(session.credentials ?? {}));
            authLogger.tokenRefresh('Token refresh successful', tokenExpirationMs);

            // Dispatch token refresh success event
            Hub.dispatch('auth', {
                event: 'tokenRefresh_success',
                data: { expirationMs: tokenExpirationMs }
            });

            return jwtToken;
        } catch (error) {
            console.error('Token refresh failed:', error);
            // Clear the refresh promise on error
            tokenCache.setRefreshPromise(null);

            // Dispatch token refresh failure event
            Hub.dispatch('auth', {
                event: 'tokenRefresh_failure',
                data: error
            });

            throw error;
        }
    })();

    // Store the refresh promise
    tokenCache.setRefreshPromise(refreshPromise);

    try {
        const token = await refreshPromise;
        // Clear the refresh promise on success
        tokenCache.setRefreshPromise(null);
        return token;
    } catch (error) {
        // If refresh fails, try one more time with a fresh request
        tokenCache.clearToken('idToken');
        return getValidToken();
    }
}

export const getAWSCredentials = async () => {
    try {
        authLogger.debug('Getting AWS credentials...');
        const authConfig = getAuthConfig();
        const cognitoLogin = `cognito-idp.${authConfig.region}.amazonaws.com/${authConfig.Cognito.userPoolId}`;

        const credentials = await fromCognitoIdentityPool({
            clientConfig: { region: authConfig.region },
            identityPoolId: authConfig.Cognito.identityPoolId,
            logins: {
                [cognitoLogin]: await getValidToken()
            }
        });

        authLogger.debug('AWS credentials obtained successfully');
        return credentials;
    } catch (error) {
        authLogger.error('Failed to get AWS credentials:', error);
        throw error;
    }
}

export const getUserId = () => sessionStorage.getItem('cognitoUserName') ?? '';